import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Building2, CheckCircle } from 'lucide-react';
import { propertiesAPI, leadsAPI } from '../services/api';
import MultiStepOnboardingForm from '../components/MultiStepOnboardingForm';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const DEVICE_DRAFT_KEY_STORAGE = 'onboarding_draft_device_key';

// Resolve a stable draft identifier for this form link.
//  - If the URL carries `?leadId=XYZ`, use that (owner-link flow).
//  - Else, mint-and-persist a device UUID in localStorage so a public
//    visitor who reopens the generic /properties/new link gets their
//    own draft back on the same device/browser.
const resolveDraftKey = (leadId) => {
  if (leadId) return `lead_${leadId}`;
  try {
    const existing = localStorage.getItem(DEVICE_DRAFT_KEY_STORAGE);
    if (existing) return existing;
    const minted = `device_${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_DRAFT_KEY_STORAGE, minted);
    return minted;
  } catch (_) {
    // Private mode / no storage — fall back to an ephemeral key so the
    // form still works for the session.
    return `device_${Math.random().toString(36).slice(2)}${Date.now()}`;
  }
};

const AddProperty = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');
  const [saving, setSaving] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(!!leadId);
  const [submitted, setSubmitted] = useState(false);
  const [draftReady, setDraftReady] = useState(false);

  // Check if user is authenticated (for conditional behavior)
  const { isAuthenticated } = useAuth();

  // Stable draft key for this form link (persists across reloads).
  const draftKeyRef = useRef(resolveDraftKey(leadId));
  const draftKey = draftKeyRef.current;
  const saveTimerRef = useRef(null);

  // Fetch any previously-saved draft and merge over leadData so the form
  // rehydrates to where the owner left off.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/onboarding/draft/${encodeURIComponent(draftKey)}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = await r.json();
        if (cancelled) return;
        if (body?.form_data && typeof body.form_data === 'object') {
          setLeadData(prev => ({ ...(prev || {}), ...body.form_data }));
        }
      } catch (_) {
        // Silent — fresh form is always acceptable.
      } finally {
        if (!cancelled) setDraftReady(true);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  // Debounced PUT /api/onboarding/draft/{key} every time formData changes.
  const handleFormDataChange = useCallback((formData) => {
    if (!draftReady) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      fetch(`${API_BASE}/api/onboarding/draft/${encodeURIComponent(draftKey)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ form_data: formData }),
      }).catch(() => { /* best-effort, silent */ });
    }, 1500);
  }, [draftKey, draftReady]);

  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  const deleteDraft = useCallback(() => {
    fetch(`${API_BASE}/api/onboarding/draft/${encodeURIComponent(draftKey)}`, {
      method: 'DELETE',
    }).catch(() => { /* silent */ });
    // Also clear the device key so a brand-new submission on the same
    // device starts a fresh draft.
    if (!leadId) {
      try { localStorage.removeItem(DEVICE_DRAFT_KEY_STORAGE); } catch (_) { /* ignore */ }
    }
  }, [draftKey, leadId]);

  useEffect(() => {
    if (leadId && isAuthenticated) {
      loadLeadData();
    }
  }, [leadId, isAuthenticated]);

  const loadLeadData = async () => {
    try {
      setLoading(true);
      const response = await leadsAPI.getById(leadId);
      const lead = response.data;
      
      // Map lead data to property format
      setLeadData({
        propertyName: lead.hotel_name || '',
        propertyType: 'Hotel',
        starRating: 3,
        address: lead.address || '',
        state: lead.state || '',
        city: lead.city || '',
        pinCode: lead.pincode || '',
        googleMapLink: lead.document_collection?.google_map_link || '',
        description: '',
        ownerName: lead.owner_name || '',
        mobile: lead.phone || '',
        email: lead.email || '',
        altContact: '',
        rooms: [],
        amenities: {},
        facilities: {},
        banking: {
          bankName: '',
          accountNumber: '',
          ifscCode: '',
          gstNumber: lead.property_details?.gst_no || ''
        },
        propertyStatus: 'Active',
        onboardingStatus: 'In Progress',
        leadId: leadId
      });
    } catch (error) {
      console.error('Error loading lead data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      
      // Clean up form data - convert empty strings to null for optional fields
      // and convert string numbers to integers for numeric fields
      const cleanFormData = (data) => {
        const cleaned = { ...data };
        
        // Convert numeric string fields to integers
        if (cleaned.totalRooms) cleaned.totalRooms = parseInt(cleaned.totalRooms, 10) || null;
        if (cleaned.starRating) cleaned.starRating = parseInt(cleaned.starRating, 10) || 3;
        if (cleaned.customCancellationDays) {
          cleaned.customCancellationDays = parseInt(cleaned.customCancellationDays, 10) || null;
        }

        // Sanitize Room Inventory payload — the form keeps numeric inputs as
        // strings (and blank until filled). Coerce them to numbers so the
        // backend RoomConfig validates. `occupancy` (legacy int field) is
        // derived from No of Adults so it is always a valid integer and never
        // an empty string (fixes the int_parsing 422).
        if (Array.isArray(cleaned.rooms)) {
          const toInt = (v) => { const n = parseInt(v, 10); return Number.isNaN(n) ? null : n; };
          const toFloat = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? null : n; };
          cleaned.rooms = cleaned.rooms.map((r) => {
            const adults = toInt(r.noOfAdults);
            return {
              ...r,
              count: toInt(r.count) ?? 0,
              basePrice: toFloat(r.basePrice) ?? 0,
              weekendPrice: toFloat(r.weekendPrice),
              extraAdultPrice: toFloat(r.extraAdultPrice),
              roomSize: toFloat(r.roomSize),
              roomSizeUnit: r.roomSizeUnit || 'sq ft',
              noOfAdults: adults,
              noOfChildren: toInt(r.noOfChildren),
              // Keep the legacy integer `occupancy` valid (= No of Adults).
              occupancy: adults ?? 1,
            };
          });
        }
        
        // Convert empty strings to null for optional fields
        const optionalStringFields = [
          'cancellationPolicy', 'customCancellationDays', 'description', 'googleMapLink', 
          'altContact', 'address', 'state', 'city', 'pinCode'
        ];
        optionalStringFields.forEach(field => {
          if (cleaned[field] === '' || cleaned[field] === undefined) {
            cleaned[field] = null;
          }
        });
        
        // Clean up empty strings in nested objects
        if (cleaned.banking) {
          Object.keys(cleaned.banking).forEach(key => {
            if (cleaned.banking[key] === '') cleaned.banking[key] = null;
          });
          // If all banking fields are null, set banking to null
          if (Object.values(cleaned.banking).every(v => v === null || v === undefined)) {
            cleaned.banking = null;
          }
        }
        
        return cleaned;
      };
      
      // If coming from a lead, include leadId
      const propertyData = leadId 
        ? cleanFormData({ ...formData, leadId })
        : cleanFormData(formData);
      
      // Use public endpoint for unauthenticated users, authenticated endpoint otherwise
      if (isAuthenticated) {
        await propertiesAPI.create(propertyData);
        // Master Form: always show the Thank You page after Submit — never
        // auto-redirect to Properties. Property is saved only on final Submit.
        setSubmitted(true);
      } else {
        // Public submission - use public endpoint that doesn't require auth
        await propertiesAPI.createPublic(propertyData);
        // Show success message for public users
        setSubmitted(true);
      }
      // Submission succeeded — clear the saved draft so a future visit
      // starts fresh (and doesn't restore the just-submitted values).
      deleteDraft();
    } catch (error) {
      console.error('Error creating property:', error);
      if (error.response?.data?.detail) {
        // Handle both string and object error details
        const detail = error.response.data.detail;
        const errorMessage = typeof detail === 'string' 
          ? detail 
          : (detail.message || detail.msg || JSON.stringify(detail));
        alert(errorMessage);
      } else {
        alert('Failed to create property. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDone = () => {
    if (isAuthenticated) {
      navigate('/properties');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading lead data...</p>
        </div>
      </div>
    );
  }

  // Success screen after submission (public Master Form)
  if (submitted) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-testid="add-property-success">
        <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-gray-200 text-center max-w-lg mx-auto">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-3" data-testid="master-form-success-title">
            Thank you!
          </h2>
          <p className="text-lg text-gray-600" data-testid="master-form-success-message">
            Master form submitted successfully
          </p>
        </div>
      </div>
    );
  }

  // For authenticated users (admin), show with header
  if (isAuthenticated) {
    return (
      <div className="space-y-6" data-testid="add-property-page">
        {/* Header for authenticated users */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/properties')}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-to-properties"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white shadow-sm">
                <Building2 size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {leadId ? 'Start Onboarding' : 'Add New Property'}
                </h2>
                <p className="text-sm text-gray-500">
                  {leadId 
                    ? 'Complete the onboarding form for this lead'
                    : 'Fill in the hotel onboarding form'
                  }
                </p>
              </div>
            </div>
          </div>
          
          {leadId && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                Creating property from Lead. Data has been pre-filled from the lead record.
              </p>
            </div>
          )}
        </div>

        {/* Form */}
        <MultiStepOnboardingForm
          initialData={leadData}
          onSave={handleSave}
          saving={saving}
          isPublic={false}
          onFormDataChange={handleFormDataChange}
        />
      </div>
    );
  }

  // Public form - clean standalone appearance
  return (
    <div className="space-y-6" data-testid="add-property-page">
      {/* Public Form Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Hotel Onboarding Form
        </h1>
        <p className="text-gray-500">
          Fill in your property details to get started with the onboarding process
        </p>
      </div>

      {/* Form */}
      <MultiStepOnboardingForm
        initialData={leadData}
        onSave={handleSave}
        saving={saving}
        isPublic={true}
        onFormDataChange={handleFormDataChange}
      />
    </div>
  );
};

export default AddProperty;
